import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { devicesActions, errorsActions, sessionActions } from '../store';
import { takeSignals } from '../store/session';
import {
  fetchDevicePage, fetchDeviceRank, fetchDevicesById, PAGE_SIZE,
} from '../common/util/deviceData';

// How long to wait before asking again where an announced device sits.
const rankRetryMs = 2000;

// The device list is a window of `total` rows in the server's order, filled
// a page at a time from GET /api/devices/page as rows come on screen. The
// devices and their trimmed positions go into the store, where the rows and
// status card read them as before; this hook keeps which page holds which
// index, and the count.
//
// A page directly after a loaded one is asked for by that page's cursor, so
// a device that reports while the list is sorted by last update moves ahead
// of the walk instead of repeating in it. A page with nothing loaded before
// it, which is how a jump to a selected row lands, is asked for by offset.
//
// A change of sort or filter starts over. Anything still in flight for the
// old query is dropped when it lands.
//
// `serverFiltered` says whether the socket subscription carries the list's
// filter. When it does, the server reports devices leaving and joining it.
export default ({
  query, sort, order, serverFiltered,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.devices.items);
  const removed = useSelector((state) => state.session.removedDevices);
  const added = useSelector((state) => state.session.addedDevices);
  const statusChanges = useSelector((state) => state.session.statusChanges);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const key = `${sort}|${order}|${query}`;
  const [state, setState] = useState({ key: null, total: null, pages: {} });

  const current = state.key === key ? state : { key, total: null, pages: {} };
  const stateRef = useRef(current);
  stateRef.current = current;

  // A new query invalidates whatever is in flight as soon as it renders,
  // before the list can ask for its pages, so the two never race.
  const keyRef = useRef(null);
  const generation = useRef(0);
  const inFlight = useRef(new Set());
  if (keyRef.current !== key) {
    keyRef.current = key;
    generation.current += 1;
    inFlight.current = new Set();
  }

  const loadPage = useCallback(async (pageIndex) => {
    const { pages, total } = stateRef.current;
    if (pages[pageIndex] || inFlight.current.has(pageIndex)) {
      return;
    }
    if (total !== null && pageIndex * PAGE_SIZE >= total) {
      return;
    }
    inFlight.current.add(pageIndex);
    const started = generation.current;
    const previous = pageIndex > 0 ? pages[pageIndex - 1] : null;
    try {
      const page = await fetchDevicePage({
        cursor: previous?.next || null,
        offset: previous?.next ? 0 : pageIndex * PAGE_SIZE,
        limit: PAGE_SIZE,
        sort,
        order,
        query,
      });
      if (started !== generation.current) {
        return;
      }
      dispatch(devicesActions.update(page.devices));
      dispatch(sessionActions.setPositions(page.positions || []));
      setState((before) => {
        const base = before.key === key ? before.pages : {};
        return {
          key,
          total: page.total,
          pages: {
            ...base,
            [pageIndex]: {
              ids: page.devices.map((device) => device.id),
              next: typeof page.next === 'string' ? page.next : null,
              removed: 0,
              lead: 0,
            },
          },
        };
      });
    } catch (error) {
      dispatch(errorsActions.push(error.message));
    } finally {
      if (started === generation.current) {
        inFlight.current.delete(pageIndex);
      }
    }
  }, [key, sort, order, query, dispatch]);

  useEffect(() => {
    loadPage(0);
  }, [key]);

  // Devices that no longer match the filter leave their pages. Each page
  // remembers how many it lost, so the rows after it move up by that much
  // and the count drops with them.
  const dropFromPages = useCallback((ids) => {
    const gone = new Set(ids);
    setState((before) => {
      if (before.key !== key) {
        return before;
      }
      let dropped = 0;
      const pages = {};
      Object.entries(before.pages).forEach(([pageIndex, page]) => {
        const kept = page.ids.filter((id) => !gone.has(id));
        dropped += page.ids.length - kept.length;
        pages[pageIndex] = { ...page, ids: kept, removed: page.removed + page.ids.length - kept.length };
      });
      return dropped ? { ...before, pages, total: Math.max(0, before.total - dropped) } : before;
    });
  }, [key]);

  // Devices that now match go in where the server ranks them. A device
  // landing inside a loaded page is spliced into it and records one row
  // fewer removed, so the rows after it move down by one. One landing
  // between loaded pages, where the pages still to load will bring it, gives
  // the next loaded page one more row of lead instead, so that page and
  // those after it move down while the gap before it grows. The count grows
  // by one either way. Each rank already counts the others, so placing them
  // lowest first puts every one where the server has it.
  //
  // Ranks from separate batches can land out of order: a device ranked
  // after one whose own rank has not landed yet points past the end of the
  // list as it stands. The final page has no page after it to bring such a
  // row, so anything at or past its start belongs to it and is appended,
  // and the one ranked ahead of it goes in before it when it lands.
  const insertIntoPages = useCallback((placements) => {
    setState((before) => {
      if (before.key !== key || before.total === null) {
        return before;
      }
      const pages = { ...before.pages };
      let { total } = before;
      [...placements].sort((a, b) => a.offset - b.offset).forEach(({ id, offset }) => {
        if (Object.values(pages).some((page) => page.ids.includes(id))) {
          return;
        }
        let shift = 0;
        let inside = null;
        let after = null;
        Object.keys(pages)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((pageIndex) => {
            const page = pages[pageIndex];
            shift -= page.lead;
            const start = pageIndex * PAGE_SIZE - shift;
            const end = start + page.ids.length;
            if (start <= offset && (offset < end || !page.next)) {
              inside = { pageIndex, at: Math.min(offset - start, page.ids.length) };
            } else if (start > offset && after === null) {
              after = pageIndex;
            }
            shift += page.removed;
          });
        if (inside) {
          const page = pages[inside.pageIndex];
          const ids = [...page.ids.slice(0, inside.at), id, ...page.ids.slice(inside.at)];
          pages[inside.pageIndex] = { ...page, ids, removed: page.removed - 1 };
        } else if (after !== null) {
          pages[after] = { ...pages[after], lead: pages[after].lead + 1 };
        }
        total += 1;
      });
      return total === before.total ? before : { ...before, pages, total };
    });
  }, [key]);

  // Asks the server where each device sits under the current sort and
  // filter, then adds the ones that match and are missing and, when asked
  // to, drops the ones that no longer match. A row spliced in whose device
  // the store has never held is fetched, so it draws; once on screen it is
  // subscribed and the stream keeps it current. Nothing is done while the
  // first page is still on its way, since it will reflect them.
  // Lets a delayed retry call the reconcile of the render it fires in.
  const reconcileRef = useRef(null);
  const reconcile = useCallback(async (ids, { dropMissing, retryUnplaced = false }) => {
    if (!ids.length || stateRef.current.total === null) {
      return;
    }
    const started = generation.current;
    const ranks = await Promise.all(ids.map(async (id) => {
      try {
        const rank = await fetchDeviceRank({
          id, sort, order, query,
        });
        return { id, offset: Number.isInteger(rank?.offset) && rank.offset >= 0 ? rank.offset : null };
      } catch (error) {
        return null;
      }
    }));
    if (started !== generation.current) {
      return;
    }
    const known = ranks.filter(Boolean);
    const placements = known.filter((rank) => rank.offset !== null);
    if (retryUnplaced) {
      const placed = new Set(placements.map((rank) => rank.id));
      const unplaced = ids.filter((id) => !placed.has(id));
      if (unplaced.length) {
        setTimeout(() => {
          if (started === generation.current) {
            reconcileRef.current(unplaced, { dropMissing: false });
          }
        }, rankRetryMs);
      }
    }
    const missing = placements.map((rank) => rank.id).filter((id) => !itemsRef.current[id]);
    if (missing.length) {
      try {
        dispatch(devicesActions.update(await fetchDevicesById(missing)));
      } catch (error) {
        // The row shows as loading until it is next fetched.
      }
      if (started !== generation.current) {
        return;
      }
    }
    if (dropMissing) {
      const gone = known.filter((rank) => rank.offset === null).map((rank) => rank.id);
      if (gone.length) {
        dropFromPages(gone);
      }
    }
    if (placements.length) {
      insertIntoPages(placements);
    }
  }, [sort, order, query, dispatch, dropFromPages, insertIntoPages]);
  reconcileRef.current = reconcile;

  // Devices the stream says no longer match the subscribed filter.
  const handledRemovalRef = useRef(removed.seq);
  useEffect(() => {
    const ids = takeSignals(removed, handledRemovalRef.current);
    handledRemovalRef.current = removed.seq;
    if (ids.length) {
      dropFromPages(ids);
    }
  }, [removed]);

  // Devices the stream announced as newly visible, or as newly matching the
  // subscribed filter. The rank endpoint can answer a moment behind the
  // stream that announced the device, so one it does not yet place is asked
  // about once more shortly after.
  const handledAdditionRef = useRef(added.seq);
  useEffect(() => {
    const ids = takeSignals(added, handledAdditionRef.current);
    handledAdditionRef.current = added.seq;
    if (ids.length) {
      reconcile(ids, { dropMissing: false, retryUnplaced: true });
    }
  }, [added]);

  // While the list's filter is not subscribed, which is when the map is not
  // set to follow it, the server cannot say which devices come to match it
  // or stop matching. The markers of the whole fleet arrive instead, so a
  // device whose status flips, or that reports for the first time, is
  // checked against the list's filter here. Only a status filter needs it.
  // Under any other a flipped device matched before and still does, and may
  // sit on a page not yet loaded, where placing it again would count it
  // twice.
  const handledStatusRef = useRef(statusChanges.seq);
  useEffect(() => {
    const ids = takeSignals(statusChanges, handledStatusRef.current);
    handledStatusRef.current = statusChanges.seq;
    if (ids.length && !serverFiltered && new URLSearchParams(query).has('status')) {
      reconcile(Array.from(new Set(ids)), { dropMissing: true });
    }
  }, [statusChanges]);

  // One slot per row in the server's order; a slot is the device when its
  // page has loaded and empty otherwise.
  const rows = useMemo(() => {
    const total = current.total ?? 0;
    const result = new Array(total);
    let shift = 0;
    Object.keys(current.pages)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((pageIndex) => {
        const page = current.pages[pageIndex];
        shift -= page.lead;
        const start = pageIndex * PAGE_SIZE - shift;
        page.ids.forEach((id, offset) => {
          const index = start + offset;
          if (index < total && items[id]) {
            result[index] = items[id];
          }
        });
        shift += page.removed;
      });
    return result;
  }, [current, items]);

  const devices = useMemo(() => rows.filter(Boolean), [rows]);
  const loaded = current.total !== null;

  return {
    rows, devices, total: current.total, loaded, loadPage,
  };
};
